import { tenantConnectionPageSchema, type TenantConnectionPage } from '@ocnexus/contracts';
import { authHeaders } from '../api.js';
export type { TenantConnectionPage } from '@ocnexus/contracts';
const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
export class ConnectionAccessRefused extends Error {}
async function request(chat: string, suffix: string, signal: AbortSignal, body?: unknown): Promise<unknown> {
  const headers = await authHeaders();
  if (!headers.Authorization) throw new ConnectionAccessRefused();
  const response = await fetch(`${API_BASE}/api/subscriber-chat/chats/${encodeURIComponent(chat)}/provider-connections${suffix}`, {
    method: body === undefined ? 'GET' : 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
    cache: 'no-store', signal, ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if ([401, 403, 404].includes(response.status)) throw new ConnectionAccessRefused();
  if (response.status !== 200) throw new Error('connection_request_unconfirmed');
  return response.json();
}
export async function readTenantConnections(chat: string, after: string | null, signal: AbortSignal): Promise<TenantConnectionPage> {
  if (after !== null && !uuid.test(after)) throw new Error('connection_cursor_invalid');
  const page = tenantConnectionPageSchema.parse(await request(chat, after ? `?after=${encodeURIComponent(after)}` : '', signal));
  const ids = page.connections.map(row => row.id.toLowerCase());
  if (new Set(ids).size !== ids.length || ids.some((id, i) => (i ? ids[i - 1] : after?.toLowerCase() ?? '') >= id)
    || (page.nextAfter !== null && (page.connections.length !== 20 || page.nextAfter !== page.connections[page.connections.length - 1]?.id))) {
    throw new Error('connection_page_invalid');
  }
  return page;
}
export async function revokeTenantConnection(chat: string, id: string, version: number, signal: AbortSignal): Promise<void> {
  if (!uuid.test(id) || !Number.isSafeInteger(version) || version <= 0) throw new Error('connection_invalid');
  const result = await request(chat, `/${encodeURIComponent(id)}/revoke`, signal, { expectedVersion: version, confirmed: true });
  if (!result || typeof result !== 'object' || Object.keys(result).length !== 1 || !('state' in result) || result.state !== 'revoked') {
    throw new Error('connection_request_unconfirmed');
  }
}
